import { AppShell } from "@astryxdesign/core/AppShell";
import { Center } from "@astryxdesign/core/Center";
import { Link } from "@astryxdesign/core/Link";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TopNav, TopNavHeading } from "@astryxdesign/core/TopNav";
import type { ReactNode } from "react";

/** Every micro-tool, so each one can point at its siblings. */
export interface ToolDef {
  id: string;
  /** Subdomain label, shown as `<label>.` in the nav and footer. */
  label: string;
  origin: string;
  /** One line, sentence case, no trailing period. */
  blurb: string;
}

export const TOOLS: ToolDef[] = [
  { id: "ip", label: "ip", origin: "https://ip.karrtopelka.com", blurb: "What the internet sees when you connect" },
  { id: "s", label: "s", origin: "https://s.karrtopelka.com", blurb: "Short links with click analytics" },
  { id: "json", label: "json", origin: "https://json.karrtopelka.com", blurb: "Format, validate and inspect JSON" },
  { id: "regex", label: "regex", origin: "https://regex.karrtopelka.com", blurb: "Test patterns against real text" },
  { id: "diff", label: "diff", origin: "https://diff.karrtopelka.com", blurb: "Compare two blocks of text" },
  { id: "epoch", label: "epoch", origin: "https://epoch.karrtopelka.com", blurb: "Convert timestamps every direction" },
];

interface Props {
  /** Which tool is rendering this shell; excluded from the sibling links. */
  active: string;
  /** Short line under the title explaining what the page does. */
  tagline: string;
  children: ReactNode;
}

/**
 * The frame every micro-tool shares: name, tagline, a centred content column and a
 * footer linking the siblings.
 *
 * Deliberately plain — these are tools people land on, use in ten seconds and leave.
 * No side nav, no dashboard chrome, one readable column.
 */
export function ToolShell({ active, tagline, children }: Props) {
  const siblings = TOOLS.filter((t) => t.id !== active);

  return (
    <AppShell
      contentPadding={0}
      mobileNav={false}
      height="auto"
      topNav={
        <TopNav
          heading={<TopNavHeading heading={`${active}.`} headingHref="/" subheading={tagline} />}
          endContent={
            <Link href="https://karrtopelka.com" isStandalone>
              <Text type="supporting" size="xsm" color="secondary">
                karrtopelka.com
              </Text>
            </Link>
          }
        />
      }
    >
      <Center axis="horizontal">
        {/* minHeight keeps short tools (epoch, ip on a failure) from leaving a bare
            strip of body colour under the content on a tall desktop window. */}
        <VStack
          gap={3}
          maxWidth={760}
          width="100%"
          padding={4}
          minHeight="calc(100dvh - 64px)"
          // Content at the top, sibling links pinned to the bottom on short pages.
          vAlign="between"
        >
          <VStack gap={3}>{children}</VStack>

          <VStack gap={1.5} paddingBlock={3}>
            <Text type="supporting" size="xsm" color="secondary">
              More tools
            </Text>
            <HStack gap={3} wrap="wrap">
              {siblings.map((t) => (
                <Link key={t.id} href={t.origin} tooltip={t.blurb} isStandalone>
                  {t.label}.
                </Link>
              ))}
            </HStack>
          </VStack>
        </VStack>
      </Center>
    </AppShell>
  );
}
